import { graphql, PageProps } from "gatsby";
import React, { useEffect, useState } from "react";
import BlogPostExcerpt from "../components/BlogPostExcerpt";
import FeaturedBlogs from "../components/FeaturedBlogs";
import PageButtons from "../components/PageButtons";
import Search from "../components/Search";
import SEO from "../components/SEO";
import { useSearchContext } from "../contexts/search";
import useJsSearch from "../util/useJsSearch";
import * as css from "./BlogList.module.css";

type Props = PageProps & {
  data: Queries.BlogPaginatedQuery;
  pageContext: Queries.SitePageContext;
};

export default function BlogList({ data, pageContext, location }: Props) {
  const { searchBlogs, setSearchBlogs } = useSearchContext();

  const { search } = useJsSearch();
  const [blogs, setBlogs] = useState(data.allMarkdownRemark.nodes);
  const [searched, setSearched] = useState(false);
  const [initialQuery, setInitialQuery] = useState("");

  useEffect(() => {
    if (searchBlogs.length) return;
    setSearchBlogs(pageContext.allBlogs);
  }, [searchBlogs, pageContext.allBlogs, setSearchBlogs]);

  useEffect(() => {
    if (!searchBlogs.length) return;
    const params = new URLSearchParams(location.search.slice(1));
    const q = params.get("q") ?? "";
    if (q !== initialQuery) {
      setSearched(false);
    }
    setInitialQuery(q);
    if (!q) {
      setBlogs(data.allMarkdownRemark.nodes);
      return;
    }
    if (q && !searched) {
      const results = search(q, searchBlogs);
      setBlogs(results);
      setSearched(true);
    }
  }, [
    searched,
    data.allMarkdownRemark.nodes,
    search,
    location.search,
    initialQuery,
    searchBlogs,
  ]);

  return (
    <div>
      {pageContext.featured && <FeaturedBlogs nodes={pageContext.featured} />}
      <Search initialQuery={initialQuery} numResults={blogs.length} />
      <div className={css.listContainer}>
        <ol data-cy="blog-list" className={css.list}>
          {blogs.map((blog, i) => (
            <BlogPostExcerpt
              data={blog}
              elementType="li"
              key={blog.fields?.slug ?? i}
            />
          ))}
        </ol>
      </div>
      {!initialQuery && <PageButtons pageContext={pageContext} />}
    </div>
  );
}

export const Head = (props: PageProps) => (
  <SEO {...props} data={{ title: "Blog" }} />
);

export const query = graphql`
  fragment NodeForBlogIndex on MarkdownRemark {
    id
    excerptAst(pruneLength: 250)
    timeToRead
    frontmatter {
      author
      authorHref
      date
      title
      tags
      description
    }
    fields {
      slug
    }
  }

  query BlogPaginated($limit: Int!, $skip: Int!) {
    allMarkdownRemark(
      sort: { frontmatter: { date: DESC } }
      filter: { frontmatter: { hide: { ne: true } } }
      limit: $limit
      skip: $skip
    ) {
      nodes {
        ...NodeForBlogIndex
      }
    }
    allSitePage {
      nodes {
        path
      }
    }
  }
`;
